import * as React from 'react';

// The puzzle banner across the top of the review, in the game bar's place
// while a puzzle is on the board: the puzzle's place among the game's, what
// it asks, whose move it is, the game in the accent, and the ways on. Drawn
// from props only; the container in `index.tsx` turns the solver's state
// into these props (`banner.ts`) and the game bar yields its row to this.

/** Phosphor "puzzle-piece", as the board tab draws it. */
const PUZZLE =
  'M220.27,158.54a8,8,0,0,0-7.7-.46,20,20,0,1,1,0-36.16A8,8,0,0,0,224,114.69V72a16,16,0,0,0-16-16H171.78a35.36,35.36,0,0,0,.22-4,36.15,36.15,0,0,0-11.36-26.25,36,36,0,0,0-60.55,23.63,36.56,36.56,0,0,0,.14,6.62H64A16,16,0,0,0,48,72v32.22a35.36,35.36,0,0,0-4-.22,36.12,36.12,0,0,0-26.24,11.36,35.7,35.7,0,0,0-9.69,27,36.08,36.08,0,0,0,33.31,33.6,35.68,35.68,0,0,0,6.62-.14V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V165.31A8,8,0,0,0,220.27,158.54ZM208,208H64V165.31a8,8,0,0,0-11.43-7.23,20,20,0,1,1,0-36.16A8,8,0,0,0,64,114.69V72h46.69a8,8,0,0,0,7.23-11.43,20,20,0,1,1,36.16,0A8,8,0,0,0,161.31,72H208v32.23a35.68,35.68,0,0,0-6.62-.14A36,36,0,0,0,204,176a35.36,35.36,0,0,0,4-.22Z';

export interface PuzzleBannerProps {
  /** "Puzzle 2 of 5 · Find the better move", "Solved · Nxe5". */
  title: string;
  /** "White to move · try 1", or after solving what the move achieves. */
  line: string;
  /** "EdBlackKnight vs mmarcin55 · Oct 9"; absent while the record loads. */
  game?: string;
  /** The puzzle is over: solved, or its solution shown. */
  done: boolean;
  /** A hint is offered: what it says when pressed. */
  onHint?: () => void;
  /** Plays the correct line on the board by itself; absent when not offered. */
  onShow?: () => void;
  /** Back to the puzzle's position for another go; absent when not offered. */
  onRetry?: () => void;
  /** Goes on to the next puzzle of the game; absent when this is the last. */
  onNext?: () => void;
  /** Ends the puzzle and returns to the game. */
  onStop: () => void;
}

export const PuzzleBanner: React.FC<PuzzleBannerProps> = ({ title, line, game, done, onHint, onShow, onRetry, onNext, onStop }) => (
  <header className={`puzzle-banner${done ? ' puzzle-banner-done' : ''}`} aria-live="polite" aria-label="Puzzle">
    <svg className="puzzle-banner-icon" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">
      <path d={PUZZLE} />
    </svg>
    <div className="puzzle-banner-text">
      <h1 className="puzzle-banner-title">{title}</h1>
      <p className="puzzle-banner-line">
        {line}
        {game && (
          <>
            <span className="puzzle-banner-dot" aria-hidden="true">
              {' · '}
            </span>
            <span className="puzzle-banner-game">{game}</span>
          </>
        )}
      </p>
    </div>
    <div className="puzzle-banner-actions">
      {onHint && (
        <button type="button" className="btn btn-ghost" onClick={onHint}>
          Hint
        </button>
      )}
      {onShow && (
        <button type="button" className="btn btn-ghost" onClick={onShow} title="Play the correct line on the board">
          Show solution
        </button>
      )}
      {onRetry && (
        <button type="button" className="btn btn-ghost" onClick={onRetry}>
          Try again
        </button>
      )}
      {onNext && (
        <button type="button" className="btn btn-primary" onClick={onNext}>
          Next puzzle <span aria-hidden="true">›</span>
        </button>
      )}
      <button type="button" className={`btn ${onNext ? 'btn-secondary' : 'btn-primary'}`} onClick={onStop}>
        Back to the game
      </button>
    </div>
  </header>
);
